import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Music,
  Mic,
  Radio,
  Volume2,
  FileMusic,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Target,
  Star,
  DollarSign,
  Smartphone,
  Headphones,
  PlayCircle,
  AlertTriangle,
  Zap,
  Plus,
  ExternalLink,
  MessageCircle,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";
import ScaleBar from "./ScaleBar";

// Sound Feature Card Component
const SoundFeatureCard = ({
  title,
  value,
  icon: Icon,
  index,
  isExpanded,
  onToggle,
  palette,
  questionTitles = {},
}) => {
  const containerRef = useRef(null);
  const delayAnimation = index * 0.1;

  // Detect if the value is an audio URL
  const isAudioURL = (text) => {
    if (!text || typeof text !== "string") return false;

    // Check if it ends with common audio extensions
    const audioExtensions = [".mp3", ".wav", ".ogg", ".m4a", ".flac"];
    return (
      audioExtensions.some((ext) => text.toLowerCase().endsWith(ext)) ||
      text.includes("soundcloud.com/") ||
      text.includes("spotify.com/") ||
      text.includes("/audio/")
    );
  };

  // Parse array values
  const parseArrayValue = (val) => {
    if (Array.isArray(val)) {
      return val;
    }

    // Check if it's a stringified array
    if (typeof val === "string" && val.startsWith("[") && val.endsWith("]")) {
      try {
        return JSON.parse(val);
      } catch (e) {
        return null;
      }
    }

    return null;
  };

  // Get example keywords if value is an array
  const examples = parseArrayValue(value);

  // If not an array, check if it's an audio URL
  const audioUrl = !examples && isAudioURL(value) ? value : null;

  // Format the text for better readability
  const formatText = (text) => {
    if (!text) return ["No information available"];

    if (typeof text !== "string") {
      if (typeof text === "object") {
        try {
          return [JSON.stringify(text, null, 2)];
        } catch (e) {
          return ["Complex object"];
        }
      }
      return [String(text)];
    }

    // If text is shorter than 200 chars, don't split it
    if (text.length < 200) return [text];

    // Split by sentences for better paragraph breaks
    const sentences = text.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";

    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 200) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });

    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  // Format text as paragraphs if it's not an audio URL or examples
  const paragraphs = !audioUrl && !examples ? formatText(value) : null;

  // Use ScaleBar for 1-10 scale values or scale objects
  const isScaleValue = (() => {
    if (
      typeof value === "object" &&
      value !== null &&
      typeof value.value === "number"
    )
      return true;
    let num = value;
    if (typeof value === "string") num = parseFloat(value);
    return typeof num === "number" && num >= 1 && num <= 10;
  })();

  // Helper to get left/right labels for scale
  const getScaleLabels = (title, value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      (value.leftLabel || value.rightLabel)
    ) {
      return {
        leftLabel: value.leftLabel || "",
        rightLabel: value.rightLabel || "",
      };
    }
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("formal") || lowerTitle.includes("casual")) {
      return { leftLabel: "Casual", rightLabel: "Formal" };
    }
    if (lowerTitle.includes("playful") || lowerTitle.includes("serious")) {
      return { leftLabel: "Serious", rightLabel: "Playful" };
    }
    if (lowerTitle.includes("modern") || lowerTitle.includes("traditional")) {
      return { leftLabel: "Traditional", rightLabel: "Modern" };
    }
    if (lowerTitle.includes("loud") || lowerTitle.includes("quiet")) {
      return { leftLabel: "Quiet", rightLabel: "Loud" };
    }
    if (lowerTitle.includes("bright") || lowerTitle.includes("dark")) {
      return { leftLabel: "Dark", rightLabel: "Bright" };
    }
    return { leftLabel: "Low", rightLabel: "High" };
  };

  return (
    <motion.div
      ref={containerRef}
      className={`rounded-xl border overflow-hidden cursor-pointer transform transition-all duration-300 ${
        isExpanded ? "col-span-full row-span-2" : ""
      }`}
      style={{
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        borderColor: isExpanded
          ? "var(--primary-color)"
          : "rgba(255, 255, 255, 0.1)",
        minHeight: isExpanded ? "auto" : "220px",
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delayAnimation }}
      onClick={onToggle}
      whileHover={{
        scale: isExpanded ? 1 : 1.02,
        borderColor: "var(--primary-color)",
        backgroundColor: "rgba(255, 255, 255, 0.05)",
      }}
    >
      {/* Card Header */}
      <div className="px-5 pt-5 pb-3 border-b border-white/10">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="p-2.5 rounded-lg"
            style={{ backgroundColor: "var(--primary-color-20)" }}
          >
            <Icon size={20} className="text-white" />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>

          {isExpanded && (
            <motion.div
              initial={{ rotate: 0 }}
              animate={{ rotate: 90 }}
              className="text-white/60 ml-auto"
            >
              <ChevronRight size={20} />
            </motion.div>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-5">
        {!isExpanded ? (
          // Collapsed View
          <div>
            {isScaleValue ? (
              (() => {
                let num = value;
                let leftLabel, rightLabel;
                if (
                  typeof value === "object" &&
                  value !== null &&
                  typeof value.value === "number"
                ) {
                  num = value.value;
                  leftLabel = value.leftLabel;
                  rightLabel = value.rightLabel;
                } else {
                  if (typeof value === "string") num = parseFloat(value);
                  const labels = getScaleLabels(title, value);
                  leftLabel = labels.leftLabel;
                  rightLabel = labels.rightLabel;
                }
                const notes = typeof value === "object" && value !== null ? value.notes : null;
                const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
                return (
                  <div>
                    <ScaleBar
                      value={num}
                      min={1}
                      max={10}
                      leftLabel={leftLabel}
                      rightLabel={rightLabel}
                      palette={palette}
                      label={title}
                      customTitle={questionTitles[title]}
                    />
                    {notes && notesVisible && (
                      <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              <>
                <p className="text-white/80 line-clamp-4 text-sm leading-relaxed">
                  {audioUrl
                    ? "Audio sample available"
                    : examples
                    ? `${examples.length} sound examples`
                    : (() => {
                        // Safely convert value to string for display
                        if (typeof value === "object" && value !== null) {
                          if (typeof value.value !== "undefined") {
                            return String(value.value);
                          }
                          if (value.description) {
                            return value.description;
                          }
                          try {
                            return JSON.stringify(value);
                          } catch (e) {
                            return "Complex object";
                          }
                        }
                        return value ? String(value) : "Not specified";
                      })()}
                </p>
                {examples && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="text-xs text-white/50">Keywords: </span>
                    {examples.slice(0, 2).map((example, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70"
                      >
                        {example}
                      </span>
                    ))}
                    {examples.length > 2 && (
                      <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">
                        +{examples.length - 2} more
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          // Expanded View
          <div>
            {isScaleValue ? (
              (() => {
                let num = value;
                let leftLabel, rightLabel;
                if (
                  typeof value === "object" &&
                  value !== null &&
                  typeof value.value === "number"
                ) {
                  num = value.value;
                  leftLabel = value.leftLabel;
                  rightLabel = value.rightLabel;
                } else {
                  if (typeof value === "string") num = parseFloat(value);
                  const labels = getScaleLabels(title, value);
                  leftLabel = labels.leftLabel;
                  rightLabel = labels.rightLabel;
                }
                const notes = typeof value === "object" && value !== null ? value.notes : null;
                const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
                return (
                  <div>
                    <ScaleBar
                      value={num}
                      min={1}
                      max={10}
                      leftLabel={leftLabel}
                      rightLabel={rightLabel}
                      palette={palette}
                      label={title}
                      customTitle={questionTitles[title]}
                    />
                    {notes && notesVisible && (
                      <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
                      </div>
                    )}
                  </div>
                );
              })()
            ) : audioUrl ? (
              <audio controls className="w-full mt-2">
                <source src={audioUrl} />
                Your browser does not support the audio element.
              </audio>
            ) : examples ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {examples.map((example, i) => (
                  <span
                    key={i}
                    className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70"
                  >
                    {example}
                  </span>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {paragraphs &&
                  paragraphs.map((paragraph, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-white/80 leading-relaxed"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Sound Frequency Visualization Component
const SoundFrequency = ({ title, icon, frequency }) => {
  // Extract actual frequency value if it's an object
  let actualFrequency = frequency;
  if (typeof frequency === "object" && frequency !== null && typeof frequency.value !== "undefined") {
    actualFrequency = frequency.value;
  }

  // Generate normalized frequency data (either from a string or an array)
  const frequencyData = useMemo(() => {
    if (!actualFrequency) return Array(20).fill(0.1); // Default minimal visualization

    if (typeof actualFrequency === "string") {
      // Parse a descriptive string to generate a pattern
      const freqStr = String(actualFrequency).toLowerCase();
      const lowPattern =
        freqStr.includes("low") ||
        freqStr.includes("bass");
      const highPattern =
        freqStr.includes("high") ||
        freqStr.includes("treble");
      const midPattern =
        freqStr.includes("mid") ||
        freqStr.includes("middle");

      // Generate a pattern based on the description
      return Array(20)
        .fill(0)
        .map((_, i) => {
          const pos = i / 20;
          if (lowPattern && pos < 0.3) return 0.5 + Math.random() * 0.5;
          if (midPattern && pos >= 0.3 && pos < 0.7)
            return 0.5 + Math.random() * 0.5;
          if (highPattern && pos >= 0.7) return 0.5 + Math.random() * 0.5;
          return 0.1 + Math.random() * 0.2; // Background noise
        });
    }

    return Array(20)
      .fill(0)
      .map(() => 0.1 + Math.random() * 0.9); // Random pattern as fallback
  }, [actualFrequency]);

  return (
    <motion.div
      className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md mt-10"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div
          className="p-2.5 rounded-lg"
          style={{ backgroundColor: "var(--primary-color-20)" }}
        >
          {icon}
        </div>
        <h3 className="text-xl font-bold text-white">{title}</h3>
      </div>

      {/* Frequency Visualization */}
      <div className="h-20 flex items-end justify-between gap-1 mb-4">
        {frequencyData.map((value, index) => (
          <motion.div
            key={index}
            className="w-full bg-gradient-to-t from-[var(--primary-color)] to-[var(--accent-color)]"
            initial={{ height: 0 }}
            animate={{ height: `${value * 100}%` }}
            transition={{
              delay: index * 0.03,
              duration: 0.5,
              repeat: Infinity,
              repeatType: "reverse",
              repeatDelay: Math.random() * 2,
            }}
            style={{
              borderRadius: "2px",
              opacity: 0.7 + value * 0.3,
            }}
          />
        ))}
      </div>

      {/* Description */}
      <p className="text-white/70 text-sm">
        {(() => {
          // Safely convert frequency to string for display
          if (frequency === undefined || frequency === null) {
            return "No frequency information available";
          }
          if (typeof frequency === "object" && frequency !== null) {
            // If it's an object, extract value property or stringify
            if (typeof frequency.value !== "undefined") {
              return String(frequency.value);
            }
            if (frequency.description) {
              return frequency.description;
            }
            try {
              return JSON.stringify(frequency);
            } catch (e) {
              return "Complex object";
            }
          }
          return String(frequency);
        })()}
      </p>
    </motion.div>
  );
};

const DynamicBrandSoundSection = ({
  sound,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  questionTitles = {},
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 2;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );

        // Add color variants
        document.documentElement.style.setProperty(
          "--primary-color-20",
          `rgba(${hexToRgb(palette.primary)}, 0.2)`
        );
        document.documentElement.style.setProperty(
          "--primary-color-30",
          `rgba(${hexToRgb(palette.primary)}, 0.3)`
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Define icons for different sound attributes
  const icons = {
    Voice: Volume2,
    Tone: Volume2,
    Sound: Music,
    Music: Music,
    Melody: FileMusic,
    Rhythm: Music,
    Tempo: Music,
    Effect: Radio,
    SFX: Radio,
    Audio: FileMusic,
    Frequency: Radio,
    Style: Music,
    Instrument: Music,
    Genre: Music,
    Vocal: Mic,
    "Voice Type": Mic,
    Radio: Radio,
    Background: Music,
    Mood: Music,
    Jingle: Music,
    "Sound Logo": Music,
  };

  // Categorize sound attributes
  const categorizeAttributes = () => {
    // Initialize categories
    const categories = {
      "Voice Elements": {},
      "Music Elements": {},
      "Sound Effects": {},
      "General Sound": {},
    };

    // Loop through all sound attributes
    Object.entries(sound).forEach(([key, value]) => {
      const lowerKey = key.toLowerCase();

      // Categorize based on keyword matching
      if (
        lowerKey.includes("voice") ||
        lowerKey.includes("tone") ||
        lowerKey.includes("speech") ||
        lowerKey.includes("narrative") ||
        lowerKey.includes("verbal")
      ) {
        categories["Voice Elements"][key] = value;
      } else if (
        lowerKey.includes("music") ||
        lowerKey.includes("melody") ||
        lowerKey.includes("rhythm") ||
        lowerKey.includes("tempo") ||
        lowerKey.includes("song") ||
        lowerKey.includes("jingle")
      ) {
        categories["Music Elements"][key] = value;
      } else if (
        lowerKey.includes("effect") ||
        lowerKey.includes("sfx") ||
        lowerKey.includes("sound effect")
      ) {
        categories["Sound Effects"][key] = value;
      } else {
        categories["General Sound"][key] = value;
      }
    });

    // Filter out empty categories
    return Object.fromEntries(
      Object.entries(categories).filter(
        ([_, values]) => Object.keys(values).length > 0
      )
    );
  };

  // Get sound cards with appropriate icons
  const getSoundCards = (soundData) => {
    return Object.entries(soundData).map(([key, value], index) => {
      // Use displayTitle if available (for duplicate titles with question IDs)
      const displayKey = (typeof value === "object" && value !== null && value.displayTitle) 
        ? value.displayTitle 
        : key;
      
      // Find exact match first
      let Icon = null;
      for (const [iconKey, iconComponent] of Object.entries(icons)) {
        if (displayKey.includes(iconKey)) {
          Icon = iconComponent;
          break;
        }
      }

      // If no match found, try lowercase matching
      if (!Icon) {
        const lowerKey = displayKey.toLowerCase();
        for (const [iconKey, iconComponent] of Object.entries(icons)) {
          if (lowerKey.includes(iconKey.toLowerCase())) {
            Icon = iconComponent;
            break;
          }
        }
      }

      // Default to Music icon if no match found
      Icon = Icon || Music;

      return {
        title: displayKey, // Use display title for duplicate titles
        value: value,
        icon: Icon,
        index,
      };
    });
  };

  const categorizedAttributes = categorizeAttributes();

  // Prepare all sound cards from all categories
  const allSoundCards = [];
  let categoryIndex = 0;

  Object.entries(categorizedAttributes).forEach(([category, attributes]) => {
    const categoryCards = getSoundCards(attributes).map((card) => ({
      ...card,
      category,
      index: card.index + categoryIndex * 10, // offset index for different categories
    }));

    allSoundCards.push(...categoryCards);
    categoryIndex++;
  });

  // If no categories found, just use all sound attributes
  if (allSoundCards.length === 0) {
    allSoundCards.push(...getSoundCards(sound));
  }

  // Function to toggle card expansion
  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  // Group items into slides for mobile navigation
  const itemSlides = [];
  for (let i = 0; i < allSoundCards.length; i += CARDS_PER_SLIDE) {
    itemSlides.push(allSoundCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < itemSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : itemSlides.length - 1));
  };

  // Find attributes that might be useful for frequency visualization
  const findFrequencyData = () => {
    // Look for specific keys that might contain frequency information
    const frequencyKeys = [
      "sound personality",
      "sound mood",
      "frequency",
      "sound quality",
      "tone",
      "texture",
      "sound character",
    ];

    for (const key of frequencyKeys) {
      for (const [attrKey, attrValue] of Object.entries(sound)) {
        if (attrKey.toLowerCase().includes(key) && attrValue) {
          return {
            key: attrKey,
            value: attrValue,
          };
        }
      }
    }

    return null;
  };

  const frequencyData = findFrequencyData();

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              // Apply transparency mask for seamless blending
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Brand Sound
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          Audio elements and sonic characteristics that define how the brand
          sounds to its audience.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">Sound Analysis</h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="brandSound"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Main Content */}
        <div className="space-y-10">
          {/* Desktop View: Sound Categories */}
          <div className="hidden md:block">
            {Object.entries(categorizedAttributes).length > 0 ? (
              Object.entries(categorizedAttributes).map(
                ([category, attributes], catIndex) => (
                  <div key={category} className="mb-16">
                    <motion.h3
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        delay: 0.2 + catIndex * 0.1,
                      }}
                      viewport={{ once: true }}
                      className="text-2xl font-bold text-white mb-6"
                    >
                      {category}
                    </motion.h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {getSoundCards(attributes).map((card, index) => (
                        <SoundFeatureCard
                          key={`${category}-${index}`}
                          title={card.title}
                          value={card.value}
                          icon={card.icon}
                          index={index + catIndex * 10} // Offset index for visual variety
                          isExpanded={
                            expandedCardIndex === `${category}-${index}`
                          }
                          onToggle={() => toggleCard(`${category}-${index}`)}
                          palette={palette}
                          questionTitles={questionTitles}
                        />
                      ))}
                    </div>
                  </div>
                )
              )
            ) : (
              // Display as a simple grid if no categories
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {allSoundCards.map((card, index) => (
                  <SoundFeatureCard
                    key={`sound-${index}`}
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    index={index}
                    isExpanded={expandedCardIndex === index}
                    onToggle={() => toggleCard(index)}
                    palette={palette}
                    questionTitles={questionTitles}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mobile View: Interactive Cards Carousel */}
          <div className="md:hidden relative">
            <div className="flex justify-between items-center mb-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronLeft
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>

              <div className="text-white/60 text-sm">
                {itemSlides.length > 0
                  ? `${activeSlide + 1} / ${itemSlides.length}`
                  : "0 / 0"}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronRight
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {itemSlides.length > 0 ? (
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {itemSlides[activeSlide].map((card, index) => (
                    <SoundFeatureCard
                      key={index}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      index={index + activeSlide * CARDS_PER_SLIDE}
                      isExpanded={
                        expandedCardIndex ===
                        index + activeSlide * CARDS_PER_SLIDE
                      }
                      onToggle={() =>
                        toggleCard(index + activeSlide * CARDS_PER_SLIDE)
                      }
                      palette={palette}
                      questionTitles={questionTitles}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
                >
                  <p className="text-white/70">
                    No content is available to display.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination dots - only show if there are multiple slides */}
            {itemSlides.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {itemSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        activeSlide === index
                          ? "var(--primary-color)"
                          : "rgba(255, 255, 255, 0.3)",
                    }}
                    onClick={() => setActiveSlide(index)}
                    whileHover={{ scale: 1.3 }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sound Frequency Visualization (if available) */}
          {frequencyData && (
            <SoundFrequency
              title={frequencyData.key}
              icon={<Radio size={22} className="text-white" />}
              frequency={frequencyData.value}
            />
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicBrandSoundSection;
